import type { TFunction } from 'i18next';
import type { RiskMatrixOverview, TemplateOverview } from '../../../api/hms';
import type { HrHome } from '../../../api/hrHome';
import type { HrWorkEnvironmentOverview } from '../../../api/hrWorkEnvironment';
import type { MeasureDeadline } from '../../../api/measureDeadlines';
import type { HrTeamSummary } from '../../../api/queries';
import { Routes } from '../../../enums/Routes';
import { fullDate } from '../hmsFormat';
import { dayAndMonth } from '../../WorkEnvironmentSurveyDetail/surveyProcessView';
import {
  coverageCounts,
  fixCards,
  requirementView,
  teamsInProcess,
} from '../HRWorkEnvironment/Overview/hrOverviewView';
import { MISSING_MEASUREMENT_DAYS } from '../HRPsychologicalSafety/hrPsychologicalSafetyView';

/*
 * The home page sums up each module in a card, and lists the next deadlines across them.
 * The rules are the modules' own. What is here only picks from them.
 */

export type HomeModule = 'workEnvironment' | 'psychologicalSafety' | 'pulse';

export type HomeData = {
  /** Left out when the organisation does not use the module */
  workEnvironment: {
    overview: HrWorkEnvironmentOverview;
    measures: MeasureDeadline[];
    riskMatrix: RiskMatrixOverview;
    template: TemplateOverview;
  } | null;
  psychologicalSafety: {
    teams: HrTeamSummary[];
    open: HrHome['psychologicalSafety']['open'];
  };
  pulse: HrHome['pulse'] | null;
};

export type HomeCard = {
  key: string;
  module: HomeModule;
  tag: string;
  color: 'success' | 'warning' | 'info';
  value: string;
  title: string;
  text: string;
  /** Where it is followed up, when there is something to see */
  link: { to: string; text: string } | null;
};

const inOrder = (t: TFunction) => t('hrHome.inOrder');

const setupRoute = (subject: 'riskMatrix' | 'template') =>
  subject === 'riskMatrix'
    ? Routes.HRRiskMatrixFullPath
    : Routes.HRRiskFactorsFullPath;

type Waiting = {
  subject: 'riskMatrix' | 'template';
  version: number;
  at: string;
};

const returnedVersions = (data: NonNullable<HomeData['workEnvironment']>) =>
  [
    data.riskMatrix.returned && {
      subject: 'riskMatrix' as const,
      version: data.riskMatrix.returned.version,
      at: data.riskMatrix.returned.returned?.returnedAt ?? '',
    },
    data.template.draft?.status === 'RETURNED' && {
      subject: 'template' as const,
      version: data.template.draft.version,
      at: data.template.draft.returned?.returnedAt ?? '',
    },
  ].filter((item): item is Waiting => Boolean(item));

const pendingVersions = (data: NonNullable<HomeData['workEnvironment']>) =>
  [
    data.riskMatrix.pending && {
      subject: 'riskMatrix' as const,
      version: data.riskMatrix.pending.version,
      at: data.riskMatrix.pending.submittedAt ?? '',
    },
    data.template.draft?.status === 'PENDING' && {
      subject: 'template' as const,
      version: data.template.draft.version,
      at: data.template.draft.submittedAt ?? '',
    },
  ].filter((item): item is Waiting => Boolean(item));

/**
 * A version the safety representative sent back waits for HR. One they have not answered yet
 * waits for them. Otherwise HR is told when the routines for whistleblowing are missing.
 */
const setupCard = (
  data: NonNullable<HomeData['workEnvironment']>,
  t: TFunction,
  language: string,
): HomeCard => {
  const returned = returnedVersions(data);
  const pending = pendingVersions(data);
  const base = { key: 'setup', module: 'workEnvironment' as const };
  const waiting = returned[0] ?? pending[0];
  const routines = data.overview.followUp.now.find(
    (item) => item.kind === 'missingRoutines',
  );
  if (!waiting && routines?.kind === 'missingRoutines')
    return {
      ...base,
      tag: t('hrOverview.fix.mustFix'),
      color: 'warning',
      value: String(routines.links.length),
      title: t('hrHome.setup.routines.title'),
      text: requirementView(routines, t, language).text,
      link: {
        to: Routes.HRRiskMatrixFullPath,
        text: t('hrHome.setup.routines.link'),
      },
    };
  if (!waiting)
    return {
      ...base,
      tag: t('hrOverview.fix.ok'),
      color: 'success',
      value: '0',
      title: t('hrHome.setup.okTitle'),
      text: t('hrHome.setup.okText'),
      link: null,
    };
  const state = returned.length > 0 ? 'returned' : 'pending';
  return {
    ...base,
    tag: t(`hrHome.setup.${state}.tag`),
    color: state === 'returned' ? 'warning' : 'info',
    value: String(state === 'returned' ? returned.length : pending.length),
    title: t(`hrHome.setup.${state}.title`),
    text: t(`hrHome.setup.${state}.text`, {
      subject: t(`safetyRep.subjects.${waiting.subject}`, {
        version: waiting.version,
      }),
      date: waiting.at ? fullDate(waiting.at, language) : '',
    }),
    link: { to: setupRoute(waiting.subject), text: t('hrHome.setup.link') },
  };
};

/** What must be followed up: the cards of the work environment, and the setup waiting for someone */
export const followUpCards = (
  data: HomeData,
  t: TFunction,
  language: string,
): HomeCard[] => {
  const work = data.workEnvironment;
  if (!work) return [];
  return [
    ...fixCards(work.overview, work.measures, t)
      .filter((card) => card.key === 'measures' || card.key === 'safetyReps')
      .map(
        (card): HomeCard => ({
          key: card.key,
          module: 'workEnvironment',
          tag: t(
            card.count > 0 ? 'hrOverview.fix.mustFix' : 'hrOverview.fix.ok',
          ),
          color: card.count > 0 ? 'warning' : 'success',
          value: String(card.count),
          title: t(`hrOverview.fix.${card.key}.title`),
          text: card.text,
          link: card.hasLink
            ? { to: card.route, text: t(`hrOverview.fix.${card.key}.link`) }
            : null,
        }),
      ),
    setupCard(work, t, language),
  ];
};

const workEnvironmentCard = (
  overview: HrWorkEnvironmentOverview,
  t: TFunction,
): HomeCard => {
  const counts = coverageCounts(overview.teams);
  const count = (coverage: string) =>
    counts.find((item) => item.coverage === coverage)?.count ?? 0;
  const due = count('overdue') + count('never');
  return {
    key: 'workEnvironment',
    module: 'workEnvironment',
    tag:
      due > 0
        ? t('hrHome.modules.workEnvironment.tag', { count: due })
        : inOrder(t),
    color: due > 0 ? 'warning' : 'success',
    value: t('hrHome.of', { count: count('ok'), total: overview.teams.length }),
    title: t('hrHome.modules.workEnvironment.title'),
    text: t('hrHome.modules.workEnvironment.text', {
      count: teamsInProcess(overview.teams).length,
    }),
    link: {
      to: Routes.HRWorkEnvironmentFullPath,
      text: t('hrHome.modules.workEnvironment.link'),
    },
  };
};

/** Measured recently enough, by the same limit as the page of psychological safety */
const measuredRecently = (team: HrTeamSummary) =>
  team.lastMeasurementDate !== null &&
  team.daysSinceLastMeasurement <= MISSING_MEASUREMENT_DAYS;

const psychologicalSafetyCard = (
  data: HomeData['psychologicalSafety'],
  t: TFunction,
): HomeCard => {
  const recent = data.teams.filter(measuredRecently).length;
  const missing = data.teams.length - recent;
  return {
    key: 'psychologicalSafety',
    module: 'psychologicalSafety',
    tag:
      missing > 0
        ? t('hrHome.modules.psychologicalSafety.tag', { count: missing })
        : inOrder(t),
    color: missing > 0 ? 'warning' : 'success',
    value: t('hrHome.of', { count: recent, total: data.teams.length }),
    title: t('hrHome.modules.psychologicalSafety.title', {
      days: MISSING_MEASUREMENT_DAYS,
    }),
    text:
      data.open.length > 0
        ? t('hrHome.modules.psychologicalSafety.open', {
            count: data.open.length,
          })
        : t('hrHome.modules.psychologicalSafety.noneOpen'),
    link: {
      to: Routes.HRPsychologicalSafetyFullPath,
      text: t('hrHome.modules.psychologicalSafety.link'),
    },
  };
};

const pulseText = (pulse: HrHome['pulse'], t: TFunction, language: string) => {
  const low = pulse.open.find((survey) => survey.lowResponse);
  if (low)
    return t('hrHome.modules.pulse.low', {
      team: low.team,
      responses: low.responses,
      expected: low.expected,
      date: dayAndMonth(low.deadline, language),
    });
  return pulse.nextRound
    ? t('hrHome.modules.pulse.next', {
        date: dayAndMonth(pulse.nextRound, language),
      })
    : t('hrHome.modules.pulse.nonePlanned');
};

const pulseCard = (
  pulse: HrHome['pulse'],
  t: TFunction,
  language: string,
): HomeCard => {
  const low = pulse.open.some((survey) => survey.lowResponse);
  return {
    key: 'pulse',
    module: 'pulse',
    tag: low ? t('hrHome.modules.pulse.tag') : inOrder(t),
    color: low ? 'info' : 'success',
    value: String(pulse.open.length),
    title: t('hrHome.modules.pulse.title', { count: pulse.open.length }),
    text: pulseText(pulse, t, language),
    link: {
      to: Routes.CustomSurveyTemplates,
      text: t('hrHome.modules.pulse.link'),
    },
  };
};

/** The status of each module the organisation uses */
export const moduleCards = (
  data: HomeData,
  t: TFunction,
  language: string,
): HomeCard[] => [
  ...(data.workEnvironment
    ? [workEnvironmentCard(data.workEnvironment.overview, t)]
    : []),
  psychologicalSafetyCard(data.psychologicalSafety, t),
  ...(data.pulse ? [pulseCard(data.pulse, t, language)] : []),
];

/** «2. okt.» */
export const dayAndShortMonth = (value: string, language: string) =>
  new Intl.DateTimeFormat(language === 'en' ? 'en-GB' : 'nb-NO', {
    day: 'numeric',
    month: 'short',
  }).format(new Date(value));

/** How far ahead the home page looks */
export const DEADLINE_WINDOW_DAYS = 30;

const MAX_DEADLINES = 8;

const DAY_MS = 24 * 60 * 60 * 1000;

type Deadline = { at: string; module: HomeModule; text: string };

const workEnvironmentDeadlines = (
  work: NonNullable<HomeData['workEnvironment']>,
  t: TFunction,
): Deadline[] => [
  ...work.overview.teams.flatMap((team) =>
    team.next && 'date' in team.next && team.next.date
      ? [
          {
            at: team.next.date,
            module: 'workEnvironment' as const,
            text: t(`hrHome.deadlines.${team.next.kind}`, { team: team.name }),
          },
        ]
      : [],
  ),
  ...work.measures
    .filter((measure) => measure.daysLeft >= 0)
    .map((measure) => ({
      at: measure.dueDate,
      module: 'workEnvironment' as const,
      text: t('hrHome.deadlines.measure', {
        measure: measure.title,
        team: measure.team,
      }),
    })),
];

const pulseDeadlines = (pulse: HrHome['pulse'], t: TFunction): Deadline[] => [
  ...pulse.open.map((survey) => ({
    at: survey.deadline,
    module: 'pulse' as const,
    text: t('hrHome.deadlines.pulse', { team: survey.team }),
  })),
  ...(pulse.nextRound
    ? [
        {
          at: pulse.nextRound,
          module: 'pulse' as const,
          text: t('hrHome.deadlines.pulseRound'),
        },
      ]
    : []),
];

const startOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

/** The deadlines of the coming weeks across the modules, the nearest first */
export const nextDeadlines = (
  data: HomeData,
  t: TFunction,
  language: string,
  now = new Date(),
) => {
  const from = startOfDay(now).getTime();
  const until = from + DEADLINE_WINDOW_DAYS * DAY_MS;
  return [
    ...(data.workEnvironment
      ? workEnvironmentDeadlines(data.workEnvironment, t)
      : []),
    ...data.psychologicalSafety.open.map((measurement) => ({
      at: measurement.deadline,
      module: 'psychologicalSafety' as const,
      text: t('hrHome.deadlines.psychologicalSafety', {
        team: measurement.team,
      }),
    })),
    ...(data.pulse ? pulseDeadlines(data.pulse, t) : []),
  ]
    .filter((deadline) => {
      const at = new Date(deadline.at).getTime();
      return at >= from && at < until;
    })
    .sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime())
    .slice(0, MAX_DEADLINES)
    .map((deadline, index) => ({
      ...deadline,
      key: `${deadline.module}-${index}`,
      date: dayAndShortMonth(deadline.at, language),
      moduleName: t(`hrHome.modules.${deadline.module}.name`),
    }));
};
